// src/pages/Contact.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

export default function Contact() {
  // --- Coordonnées ---
  const CONTACT_EMAIL = "contact@mameche.tg";
  const PHONES = [
    // { label: "(+228) 22 25 86 16", digits: "22822258616" },
    // { label: "(+228) 98 11 05 55", digits: "22898110555" },
    { label: "(+228) 96 59 55 55", digits: "22896595555" },
    { label: "(+228) 90 11 92 55", digits: "22890119255" },
  ];
  const ADDRESS =
    "Cité Maman N’DANIDA, à côté de la pharmacie de la Cité — Avénou, 08 BP 8213 Lomé 08, Lomé (Togo)";
  const MAP_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`;

  // --- Form state ---
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subjectType: "Demande de devis",
    dept: "medical",
    message: "",
    whatsappTo: PHONES[1].digits,
  });
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // --- Pré-remplissage via query params ---
  const { search } = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(search);

    const subjectType = q.get("subjectType") || undefined; // ex: "Demande de devis"
    const dept = q.get("dept") || undefined; // "medical" | "industrial"
    const name = q.get("name") || undefined;
    const email = q.get("email") || undefined;
    const phone = q.get("phone") || undefined;
    const whatsappTo = q.get("whatsappTo") || undefined;

    // Si "message" absent mais product/ref fournis → construire un message
    const product = q.get("product") || "";
    const ref = q.get("ref") || "";
    const queryMsg = q.get("message");
    const builtMsg =
      queryMsg ||
      (product || ref
        ? `Bonjour,\n\nJe souhaite un devis (${dept === "industrial" ? "Département Industriel" : "Département Médical"}).\nProduit : ${product || "—"}\nRéférence : ${ref || "—"}\nQuantité : __\nDélai souhaité : __\n\nCordialement,`
        : "");

    setForm((f) => ({
      ...f,
      subjectType: subjectType || f.subjectType,
      dept: dept || f.dept,
      name: name ?? f.name,
      email: email ?? f.email,
      phone: phone ?? f.phone,
      whatsappTo: whatsappTo || f.whatsappTo,
      message: builtMsg || f.message,
    }));
  }, [search]);

  // Helpers validation + liens
  const isEmail = (v) => /\S+@\S+\.\S+/.test(v);
  const isValid = form.name.trim() && isEmail(form.email) && form.message.trim().length >= 10;

  const deptLabel = form.dept === "industrial" ? "Industriel" : "Médical";
  const mailSubject = `${form.subjectType} — ${deptLabel} — ${form.name || "Contact"}`;
  const mailBody = useMemo(
    () =>
      `Bonjour MAMECHE,

Nom / Organisation : ${form.name}
Email : ${form.email}
Téléphone : ${form.phone || "—"}
Département : ${deptLabel}
Sujet : ${form.subjectType}

Message :
${form.message}

Cordialement,
${form.name}`,
    [form, deptLabel]
  );
  const mailHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    mailSubject
  )}&body=${encodeURIComponent(mailBody)}`;
  const waText =
    `Bonjour MAMECHE,%0A%0A${form.subjectType} — ${deptLabel}%0A` +
    `Nom/Org.: ${form.name}%0AEmail: ${form.email}%0ATéléphone: ${form.phone || "—"}%0A%0A` +
    `Message : %0A${encodeURIComponent(form.message)}`;
  const waHref = `https://wa.me/${form.whatsappTo}?text=${waText}`;

  return (
    <div className="container py-5">
      <div className="row g-4">
        {/* FORMULAIRE */}
        <div className="col-lg-7">
          <h2 className="fw-bold">Contact & Devis</h2>
          <p className="text-muted mb-3">Pré-rempli depuis la fiche produit si vous venez de là 😊</p>

          <form className="card card-body shadow-sm">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Nom / Organisation</label>
                <input name="name" className="form-control" value={form.name} onChange={onChange} required />
              </div>
              <div className="col-md-6">
                <label className="form-label">Email</label>
                <input
                  name="email"
                  type="email"
                  className={"form-control " + (form.email && !isEmail(form.email) ? "is-invalid" : "")}
                  value={form.email}
                  onChange={onChange}
                  required
                />
                {form.email && !isEmail(form.email) && <div className="invalid-feedback">Adresse email invalide.</div>}
              </div>

              <div className="col-md-6">
                <label className="form-label">Téléphone (optionnel)</label>
                <input name="phone" className="form-control" value={form.phone} onChange={onChange} placeholder="+228…" />
              </div>
              <div className="col-md-6">
                <label className="form-label">Département</label>
                <select name="dept" className="form-select" value={form.dept} onChange={onChange}>
                  <option value="medical">Médical</option>
                  <option value="industrial">Industriel</option>
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Sujet</label>
                <select name="subjectType" className="form-select" value={form.subjectType} onChange={onChange}>
                  <option>Demande de devis</option>
                  <option>Renseignement produit</option>
                  <option>Service après-vente</option>
                  <option>Partenariat</option>
                  <option>Autre</option>
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label">Envoyer via WhatsApp</label>
                <select name="whatsappTo" className="form-select" value={form.whatsappTo} onChange={onChange}>
                  {PHONES.map((p) => (
                    <option key={p.digits} value={p.digits}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <label className="form-label">Votre message</label>
                <textarea
                  name="message"
                  rows={6}
                  className="form-control"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Décrivez votre besoin (référence, quantité, délai, lieu de livraison, etc.)"
                  required
                />
                <div className="form-text">
                  {form.message.length < 10 ? "Ajoutez quelques détails (≥ 10 caractères)." : "Parfait, votre message est prêt."}
                </div>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-3">
              <a className={"btn btn-success " + (!isValid ? "disabled" : "")} href={isValid ? mailHref : undefined}>
                Envoyer par Email
              </a>
              <a
                className={"btn btn-outline-secondary " + (!isValid ? "disabled" : "")}
                href={isValid ? waHref : undefined}
                target="_blank"
                rel="noreferrer"
              >
                WhatsApp
              </a>
            </div>
          </form>
        </div>

        {/* COORDONNÉES & CARTE */}
        <div className="col-lg-5">
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              <h3 className="h5 fw-bold mb-3">Coordonnées</h3>
              <div className="mb-2">
                <div className="fw-semibold">Adresse</div>
                <div className="text-muted small">{ADDRESS}</div>
              </div>
              <div className="mb-2">
                <div className="fw-semibold">Téléphones</div>
                <div className="d-flex flex-wrap gap-2">
                  {PHONES.map((p) => (
                    <a key={p.digits} className="btn btn-outline-secondary btn-sm" href={`tel:+${p.digits}`}>
                      {p.label}
                    </a>
                  ))}
                </div>
              </div>
              <div className="mb-0">
                <div className="fw-semibold">Email</div>
                <a href={`mailto:${CONTACT_EMAIL}`} className="small">
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </div>

          <div className="ratio ratio-4x3 rounded overflow-hidden border">
            <iframe title="Carte MAMECHE" src={MAP_EMBED} loading="lazy" style={{ border: 0 }} />
          </div>
        </div>
      </div>
    </div>
  );
}